import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { AlertTriangle, CheckCircle, X, RefreshCw, User } from 'lucide-react';
import ExportButtons from './ExportButtons';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Textarea } from '../ui/textarea';

const ReportedTrainersManager = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [adminMessage, setAdminMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchReports();
  }, [statusFilter]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = statusFilter 
        ? `${API_BASE}/api/admin/trainers/reports?status=${statusFilter}`
        : `${API_BASE}/api/admin/trainers/reports`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      } else {
        toast({
          title: 'Error',
          description: 'Failed to fetch reports',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch reports',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const openActionDialog = (report, action) => {
    setSelectedReport(report);
    setSelectedAction(action);
    setAdminMessage('');
    setIsDialogOpen(true);
  };

  const handleSubmitAction = async () => {
    if (!adminMessage.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a message for the user',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      const newStatus = selectedAction === 'review' ? 'reviewed' : 'dismissed';
      
      const response = await fetch(`${API_BASE}/api/admin/trainers/reports/${selectedReport.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          status: newStatus,
          admin_message: adminMessage
        })
      });

      if (response.ok) {
        toast({
          title: 'Success',
          description: `Report ${newStatus} and user notified via email`
        });
        setIsDialogOpen(false);
        setAdminMessage('');
        setSelectedReport(null);
        setSelectedAction(null);
        fetchReports();
      } else {
        const error = await response.json();
        toast({
          title: 'Error',
          description: error.detail || 'Failed to update report',
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error updating report:', error);
      toast({
        title: 'Error',
        description: 'Failed to update report',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getReasonLabel = (reason) => {
    const labels = {
      'inappropriate_content': 'Inappropriate Content',
      'spam': 'Spam',
      'harassment': 'Harassment',
      'fake_profile': 'Fake Profile',
      'other': 'Other'
    };
    return labels[reason] || reason;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold dark:text-gray-100">Reported Trainers</h2>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
            Manage reports against trainers
          </p>
        </div>
        <Button onClick={fetchReports} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={reports}
        columns={[
          { header: 'ID', accessor: (row) => row.id || 'N/A' },
          { header: 'Reporter', accessor: (row) => row.reporter_name || 'N/A' },
          { header: 'Trainer', accessor: (row) => row.trainer_name || 'N/A' },
          { header: 'Reason', accessor: (row) => getReasonLabel(row.reason) },
          { header: 'Status', accessor: (row) => row.status || 'N/A' },
          { header: 'Created At', accessor: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A' }
        ]}
        filename="reported-trainers"
      />

      {/* Filter Tabs */}
      <div className="flex gap-2">
        <Button
          variant={statusFilter === 'pending' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('pending')}
          size="sm"
        >
          Pending
        </Button>
        <Button
          variant={statusFilter === 'reviewed' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('reviewed')}
          size="sm"
        >
          Reviewed
        </Button>
        <Button
          variant={statusFilter === 'resolved' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('resolved')}
          size="sm"
        >
          Resolved
        </Button>
        <Button
          variant={statusFilter === 'dismissed' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('dismissed')}
          size="sm"
        >
          Dismissed
        </Button>
        <Button
          variant={statusFilter === '' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('')}
          size="sm"
        >
          All
        </Button>
      </div>

      {/* Reports Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {reports.length === 0 ? (
          <Card className="col-span-full">
            <CardContent className="p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">No reports found</p>
            </CardContent>
          </Card>
        ) : (
          reports.map((report) => (
            <Card key={report.id} className="dark:bg-gray-800 dark:border-gray-700">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg dark:text-gray-100 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                      {getReasonLabel(report.reason)}
                    </CardTitle>
                    <div className="mt-2 space-y-1">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <User className="w-4 h-4 inline mr-1" />
                        Trainer: <span className="font-medium">{report.trainer_name}</span>
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Reporter: {report.reporter_name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 dark:text-gray-400">
                        {new Date(report.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <Badge variant={report.status === 'pending' ? 'warning' : 'default'}>
                    {report.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                {report.reason_details && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                    {report.reason_details}
                  </p>
                )}
                {report.status === 'pending' && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => openActionDialog(report, 'review')}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Review
                    </Button>
                    <Button
                      onClick={() => openActionDialog(report, 'dismiss')}
                      size="sm"
                      variant="outline"
                      className="flex-1"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Dismiss
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Action Dialog with Message */}
      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl font-bold text-gray-800">
              {selectedAction === 'review' ? 'Review Report' : 'Dismiss Report'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-600 dark:text-gray-400 space-y-2">
              <p>
                {selectedAction === 'review' 
                  ? 'You are reviewing this report. Please provide a message to inform the reporter about your decision.'
                  : 'You are dismissing this report. Please provide a message to inform the reporter about your decision.'}
              </p>
              {selectedReport && (
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg mt-2 space-y-2">
                  <p className="text-sm font-medium">Reporter: {selectedReport.reporter_name}</p>
                  <p className="text-sm">Trainer: {selectedReport.trainer_name}</p>
                  <p className="text-sm">Reason: {getReasonLabel(selectedReport.reason)}</p>
                  {selectedReport.reason_details && (
                    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">Report Details:</p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-600">
                        {selectedReport.reason_details}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="my-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Message to Reporter *
            </label>
            <Textarea
              value={adminMessage}
              onChange={(e) => setAdminMessage(e.target.value)}
              placeholder="Enter your message to the reporter..."
              className="w-full min-h-[120px] resize-y"
              disabled={submitting}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              This message will be sent to the reporter via email.
            </p>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSubmitAction}
              disabled={submitting || !adminMessage.trim()}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? 'Sending...' : `${selectedAction === 'review' ? 'Review' : 'Dismiss'} & Notify`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ReportedTrainersManager;